import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { IUserWithMetadata } from '@/types/Auth/User.types';
import { LOCAL_DEV_USER } from '@zero-dash/types';
import { useZeroDashSdk } from '@/contexts/ZeroDashSdkContext';

interface IUseUserListReturn {
  users: IUserWithMetadata[];
  loading: boolean;
  error?: string;
}

/** How long a warm directory cache is reused before the next mount refetches it. */
const CACHE_TTL_MS = 5 * 60 * 1000;

/** Delay before a failed directory fetch is automatically re-attempted. */
const FAILED_RETRY_DELAY_MS = 30 * 1000;

/**
 * Process-wide cache of the user directory. The cache holds the resolved list,
 * the time it was fetched (so it expires after CACHE_TTL_MS), and an in-flight
 * promise so concurrent mounts share one call.
 */
let usersCache: IUserWithMetadata[] | null = null;
let usersCachedAt = 0;
let inFlightUsers: Promise<IUserWithMetadata[]> | null = null;

const isCacheFresh = (): boolean => (
  usersCache !== null && Date.now() - usersCachedAt < CACHE_TTL_MS
);

/**
 * Fetches the full user directory, **including deactivated users**. The cache is
 * invalidated after CACHE_TTL_MS so directory changes propagate, and a failed
 * fetch is retried automatically after FAILED_RETRY_DELAY_MS.
 */
export function useUserList(): IUseUserListReturn {
  const zeroDashSdk = useZeroDashSdk();

  const [users, setUsers] = useState<IUserWithMetadata[]>(isCacheFresh() ? usersCache ?? [] : []);
  const [loading, setLoading] = useState<boolean>(!isCacheFresh());
  const [error, setError] = useState<string>();
  const [retryToken, setRetryToken] = useState<number>(0);

  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    let isMounted = true;

    if (import.meta.env.VITE_DISABLE_AUTH === 'true') {
      setUsers([{ ...(LOCAL_DEV_USER as unknown as IUserWithMetadata), isActive: true }]);
      setLoading(false);
      return undefined;
    }

    if (isCacheFresh()) {
      setUsers(usersCache ?? []);
      setLoading(false);
      return undefined;
    }

    async function loadUsers(): Promise<void> {
      setLoading(true);
      setError(undefined);

      try {
        if (!inFlightUsers) {
          inFlightUsers = zeroDashSdk.services.auth
            .getUsers({ includeInactive: true })
            .then((resp) => {
              const sorted = [...resp].sort((a, b) => a.givenName.localeCompare(b.givenName));
              usersCache = sorted;
              usersCachedAt = Date.now();
              return sorted;
            })
            .finally(() => {
              inFlightUsers = null;
            });
        }

        const result = await inFlightUsers;

        if (isMounted) setUsers(result);
      } catch {
        if (isMounted) {
          setError('An error occurred while fetching users');
          retryTimerRef.current = setTimeout(
            () => setRetryToken((token) => token + 1),
            FAILED_RETRY_DELAY_MS,
          );
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadUsers();

    return () => {
      isMounted = false;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [zeroDashSdk.services.auth, retryToken]);

  return useMemo<IUseUserListReturn>(() => ({
    users,
    loading,
    error,
  }), [users, loading, error]);
}
